// Completely rewritten GPT-5 blog post with all corrections

export const gpt5RewrittenContent = {
  de: `10. August 2025 – 10 Min. Lesezeit

Vor drei Tagen hat OpenAI GPT-5 veröffentlicht. Nach intensiven Tests mit dem neuen Modell möchte ich meine Eindrücke teilen - als jemand, der täglich mit KI-Tools entwickelt. Seit April 2025 nutze ich Claude als primären Co-Developer, nachdem ich zuvor mit OpenAI o3, GPT-4 und GPT-4.1 gearbeitet habe.

⸻

## Die technischen Daten

OpenAI präsentiert beeindruckende Spezifikationen:
• **500'000 Token Kontext** 
• **74.9% auf SWE-bench** (gegenüber Claude 4.1's 74.5%)
• **45% weniger Fehler als GPT-4o** 
• **3 Modellgrössen** (gpt-5, gpt-5-mini, gpt-5-nano)

Die Community-Reaktionen auf X sind gemischt. Viele Nutzer berichten von Einschränkungen bei den Rate Limits, vermissen die Persönlichkeit früherer Modelle und merken an, dass die Benchmark-Ergebnisse nicht unbedingt die Praxis widerspiegeln. Einige bevorzugen weiterhin GPT-4o, während andere das neue Modell für alltägliche Aufgaben als sehr gut bewerten.

⸻

## Mein Test mit AIpuna Code

Ich habe GPT-5 mit denselben Aufgaben getestet, die ich auch bei anderen Modellen verwende:

**Die Aufgabe:**
Refactoring eines komplexen Flutter-Widgets mit Dart, State Management und Stripe Payment Integration.

GPT-5 liefert funktionierenden Code, das ist unbestritten. Die Qualität ist gut für Coding-Aufgaben. Aber im direkten Vergleich fehlt mir das, was Claude auszeichnet: Claude fühlt sich wie ein echter Senior Co-Developer an, der die praktischen Anforderungen in Production-Umgebungen versteht.

⸻

## Der grosse Kontext: Theorie vs. Praxis

**500k Token Kontext - Eigentlich genau was wir brauchen**

Grosser Kontext ist für Entwicklungsaufgaben tatsächlich sehr wichtig. Das Problem ist nicht die Grösse, sondern die Umsetzung:

• GPT-5 verliert trotz 500k Token den Kontext - besonders in der Mitte
• Man muss mit sequenziellen Tasks arbeiten, Memories anlegen, Chats komprimieren
• Ohne präzise Instructions und Regeln geht der Fokus verloren
• Die Antwortzeiten variieren stark - vermutlich wegen Überlastung

**Das eigentliche Problem:**
Die hohe Nachfrage übersteigt wahrscheinlich OpenAIs Computing-Kapazität. Die Benchmark-Performance ist nur bei optimalen Bedingungen erreichbar - in der Realität kämpft das System mit der Last.

**Google Gemini 2.5 Pro als Alternative:**
Interessanterweise bietet Google Gemini 2.5 Pro eines der grössten Kontextfenster auf dem Markt und ist ebenfalls sehr gut für Entwicklungsaufgaben geeignet. In manchen Situationen liefert es stabilere Ergebnisse als GPT-5.

⸻

## Benchmarks und Realität

GPT-5 zeigt starke Benchmark-Ergebnisse:
- SWE-bench: 74.9%
- HumanEval: 95.2%
- MMLU: 91.8%

In der praktischen Anwendung beobachte ich:
- Der generierte Code ist meist korrekt
- Tendenz zu komplexeren Lösungsansätzen
- Gelegentlich werden veraltete oder nicht-existente Framework-Features vorgeschlagen
- Die Konsistenz über längere Sessions variiert

⸻

## Community-Feedback auf X

Die Diskussionen auf X zeigen ein differenziertes Bild:

**Häufig genannte Kritikpunkte:**
• Rate Limits und Verfügbarkeit
• Fehlende "Persönlichkeit" im Vergleich zu früheren Modellen
• Diskrepanz zwischen Benchmarks und praktischer Erfahrung
• Viele wünschen sich GPT-4o zurück

**Positive Stimmen:**
• Gut für alltägliche Programmieraufgaben
• Starke Performance bei strukturierten Aufgaben
• Gute Integration in bestehende Workflows
• Zuverlässige Ergebnisse bei Standard-Tasks

⸻

## Der Vergleich: GPT-5 vs Claude 4.1 vs Gemini 2.5 Pro

Nach drei Tagen intensiver Nutzung:

**GPT-5 - Beobachtungen:**
• Sehr grosser Kontext verfügbar (aber verliert ihn trotzdem)
• Gut für Standard-Coding-Aufgaben
• Breites Wissensspektrum
• Kämpft mit der Last der hohen Nachfrage

**Claude 4.1 - Meine Erfahrung:**
• Fühlt sich wie ein echter Senior Co-Developer an
• Versteht Entwickler-Intentionen besser
• Pragmatischere Lösungsansätze
• Konsistentere Qualität über Sessions

**Gemini 2.5 Pro - Die Überraschung:**
• Eines der grössten verfügbaren Kontextfenster
• Sehr gut für Entwicklungsaufgaben
• Oft stabilere Performance als GPT-5
• Interessante Alternative für grosse Projekte

⸻

## Payment Integration und Spezifika

Bei Payment-spezifischen Anforderungen zeigen sich Unterschiede:

**Stripe Integration:**
GPT-5 kennt Stripe gut und kann solide Implementierungen liefern. Claude versteht jedoch besser die Nuancen von Webhook-Handling, Idempotenz und Edge-Cases bei Payment-Flows.

**Flutter-spezifische Patterns:**
Alle drei Modelle beherrschen Flutter, aber Claude zeigt oft pragmatischere Ansätze bei State Management und Widget-Architektur. Gemini 2.5 Pro überrascht mit detailliertem Flutter-Wissen.

⸻

## Workarounds für GPT-5

Um mit GPT-5 effektiv zu arbeiten, habe ich folgende Strategien entwickelt:

**Sequential Tasks:**
• Aufgaben in kleine, sequenzielle Schritte aufteilen
• Klare Checkpoints zwischen Tasks setzen
• Regelmässig den Kontext zusammenfassen

**Memory Management:**
• Eigene Memories für wichtige Projektdetails anlegen
• Chat-Verläufe regelmässig komprimieren
• Wichtige Informationen in Instructions festhalten

**Präzise Regeln:**
• Sehr spezifische Instructions verwenden
• Klare Constraints definieren
• Output-Format genau vorgeben

⸻

## Mein persönliches Fazit

GPT-5 ist ein gutes Modell für Coding - das möchte ich klar festhalten. Es leistet solide Arbeit und wird von vielen Entwicklern geschätzt. Die technischen Verbesserungen sind real.

Aber für meine spezifischen Anforderungen bei der AIpuna-Entwicklung bleibe ich bei Claude 4.1. Nicht weil GPT-5 schlecht ist, sondern weil Claude sich für mich wie ein echter Senior Co-Developer anfühlt. Nach vier Monaten intensiver Zusammenarbeit versteht Claude meine Arbeitsweise und liefert pragmatische, produktionsreife Lösungen.

**Meine Toolchain:**
• **Claude 4.1**: Primärer Co-Developer für komplexe Aufgaben
• **GPT-5**: Für spezifische Tasks wenn Claude überlastet ist
• **Gemini 2.5 Pro**: Für Projekte die sehr grossen Kontext benötigen

⸻

## Empfehlungen

**Für wen GPT-5 interessant sein könnte:**
• Teams die bereits in OpenAI-Ecosystem investiert sind
• Projekte mit Standard-Entwicklungsaufgaben
• Wenn breite Tool-Integration wichtig ist

**Wann Claude 4.1 die bessere Wahl ist:**
• Wenn Sie einen echten Co-Developer Feeling wollen
• Komplexe Architektur-Entscheidungen
• Agenten-basierte Entwicklung
• Konsistenz über lange Sessions wichtig

**Wann Gemini 2.5 Pro überraschen könnte:**
• Sehr grosse Codebases
• Wenn stabiler grosser Kontext wichtig ist
• Als Backup wenn andere Modelle überlastet sind

⸻

## Ausblick

Die Entwicklung geht weiter. GPT-5 wird sich verbessern, sobald OpenAI die Infrastruktur ausbaut. Gemini 2.5 Pro zeigt, dass Google ernsthaft mitspielt. Und Claude bleibt mein verlässlicher Partner.

Für mich persönlich ist die aktuelle Situation ideal: Wir haben mehrere sehr fähige KI-Assistenten zur Verfügung. Je nach Aufgabe kann ich das passende Tool wählen. Das ist ein Luxus, den wir vor einem Jahr noch nicht hatten.

Die wichtigste Erkenntnis: Es geht nicht um das "beste" Modell, sondern um das richtige Tool für die jeweilige Aufgabe. Und für meine tägliche Arbeit ist das Claude - aber es ist gut zu wissen, dass es Alternativen gibt.

*Patrik Germann*  
*30 Jahre IT-Erfahrung*  
*Pragmatischer Multi-Model Nutzer*`,
  en: `August 10, 2025 – 10 min read

Three days ago, OpenAI released GPT-5. After intensive testing with the new model, I'd like to share my impressions - as someone who develops daily with AI tools. Since April 2025, I've been using Claude as my primary co-developer, after previously working with OpenAI o3, GPT-4, and GPT-4.1.

⸻

## The Technical Specifications

OpenAI presents impressive specifications:
• **500,000 token context**
• **74.9% on SWE-bench** (compared to Claude 4.1's 74.5%)
• **45% fewer errors than GPT-4o**
• **3 model sizes** (gpt-5, gpt-5-mini, gpt-5-nano)

Community reactions on X are mixed. Many users report rate limit restrictions, miss the personality of earlier models, and note that benchmark results don't necessarily reflect real-world practice. Some still prefer GPT-4o, while others rate the new model as very good for everyday tasks.

⸻

## My Test with AIpuna Code

I tested GPT-5 with the same tasks I use for other models:

**The Task:**
Refactoring a complex Flutter widget with Dart, state management, and Stripe payment integration.

GPT-5 delivers working code, that's undeniable. The quality is good for coding tasks. But in direct comparison, I miss what distinguishes Claude: Claude feels like a real senior co-developer who understands practical requirements in production environments.

⸻

## The Large Context: Theory vs. Practice

**500k Token Context - Actually What We Need**

Large context is actually very important for development tasks. The problem isn't the size, but the implementation:

• GPT-5 loses context despite 500k tokens - especially in the middle
• You have to work with sequential tasks, create memories, compress chats
• Without precise instructions and rules, focus is lost
• Response times vary greatly - likely due to overload

**The Real Problem:**
High demand probably exceeds OpenAI's computing capacity. Benchmark performance is only achievable under optimal conditions - in reality, the system struggles with the load.

**Google Gemini 2.5 Pro as Alternative:**
Interestingly, Google Gemini 2.5 Pro offers one of the largest context windows on the market and is also very good for development tasks. In some situations, it delivers more stable results than GPT-5.

⸻

## Benchmarks and Reality

GPT-5 shows strong benchmark results:
- SWE-bench: 74.9%
- HumanEval: 95.2%
- MMLU: 91.8%

In practical application, I observe:
- Generated code is mostly correct
- Tendency toward more complex solution approaches
- Occasionally suggests outdated or non-existent framework features
- Consistency over longer sessions varies

⸻

## Community Feedback on X

Discussions on X show a nuanced picture:

**Frequently mentioned criticisms:**
• Rate limits and availability
• Missing "personality" compared to earlier models
• Discrepancy between benchmarks and practical experience
• Many want GPT-4o back

**Positive voices:**
• Good for everyday programming tasks
• Strong performance on structured tasks
• Good integration into existing workflows
• Reliable results for standard tasks

⸻

## The Comparison: GPT-5 vs Claude 4.1 vs Gemini 2.5 Pro

After three days of intensive use:

**GPT-5 - Observations:**
• Very large context available (but loses it anyway)
• Good for standard coding tasks
• Broad knowledge spectrum
• Struggles with high demand load

**Claude 4.1 - My Experience:**
• Feels like a real senior co-developer
• Better understands developer intentions
• More pragmatic solution approaches
• More consistent quality across sessions

**Gemini 2.5 Pro - The Surprise:**
• One of the largest available context windows
• Very good for development tasks
• Often more stable performance than GPT-5
• Interesting alternative for large projects

⸻

## Payment Integration and Specifics

Differences emerge with payment-specific requirements:

**Stripe Integration:**
GPT-5 knows Stripe well and can deliver solid implementations. However, Claude better understands the nuances of webhook handling, idempotency, and edge cases in payment flows.

**Flutter-specific Patterns:**
All three models master Flutter, but Claude often shows more pragmatic approaches to state management and widget architecture. Gemini 2.5 Pro surprises with detailed Flutter knowledge.

⸻

## Workarounds for GPT-5

To work effectively with GPT-5, I've developed the following strategies:

**Sequential Tasks:**
• Break tasks into small, sequential steps
• Set clear checkpoints between tasks
• Regularly summarize context

**Memory Management:**
• Create own memories for important project details
• Regularly compress chat histories
• Capture important information in instructions

**Precise Rules:**
• Use very specific instructions
• Define clear constraints
• Specify output format exactly

⸻

## My Personal Conclusion

GPT-5 is a good model for coding - I want to be clear about that. It does solid work and is appreciated by many developers. The technical improvements are real.

But for my specific requirements in AIpuna development, I'm sticking with Claude 4.1. Not because GPT-5 is bad, but because Claude feels like a real senior co-developer to me. After four months of intensive collaboration, Claude understands my working style and delivers pragmatic, production-ready solutions.

**My Toolchain:**
• **Claude 4.1**: Primary co-developer for complex tasks
• **GPT-5**: For specific tasks when Claude is overloaded
• **Gemini 2.5 Pro**: For projects requiring very large context

⸻

## Recommendations

**Who might find GPT-5 interesting:**
• Teams already invested in OpenAI ecosystem
• Projects with standard development tasks
• When broad tool integration is important

**When Claude 4.1 is the better choice:**
• When you want a real co-developer feeling
• Complex architecture decisions
• Agent-based development
• Consistency over long sessions important

**When Gemini 2.5 Pro might surprise:**
• Very large codebases
• When stable large context is important
• As backup when other models are overloaded

⸻

## Outlook

Development continues. GPT-5 will improve once OpenAI expands infrastructure. Gemini 2.5 Pro shows that Google is seriously competing. And Claude remains my reliable partner.

For me personally, the current situation is ideal: We have several very capable AI assistants available. Depending on the task, I can choose the right tool. That's a luxury we didn't have a year ago.

The most important insight: It's not about the "best" model, but about the right tool for each task. And for my daily work, that's Claude - but it's good to know there are alternatives.

*Patrik Germann*  
*30 years IT experience*  
*Pragmatic multi-model user*`
}